<template>
  <div class="e-module e-module--limited">
    <main class="e-chat"
        ref="dropArea"
        @dragover.prevent="() => {if (!uploadActiveChat) {return}$refs.dropArea.classList.add('on-drag')}"
        @dragleave="() => {if (!uploadActiveChat) {return}$refs.dropArea.classList.remove('on-drag')}"
        @dragend="() => {if (!uploadActiveChat) {return}$refs.dropArea.classList.remove('on-drag')}"
        @drop.stop.prevent="event => {if (!uploadActiveChat) {return} $refs.dropArea.classList.remove('on-drag'); handleDrop(event)}">
      <div class="drop-overlay"></div>
      <header class="e-chat__header">
        <div :class="[
          'e-chat__title',
          {
            'chat-is-pending':
              currentUserChat && currentUserChat.status === 'pending'
          }
        ]">
          {{ chatTitle }}
        </div>
        <div class="e-chat__header-buttons">
          <volume-button v-if="currentUserChat" />
          <el-tooltip v-if="currentUserChat" :content="currentUserChat.status === 'active'
              ? $t('chat.close')
              : $t('chat.cancel')
            " class="item" effect="dark" placement="top">
            <icon-close class="e-chat__close" @click="finishChat" />
          </el-tooltip>
        </div>
      </header>
      <div class="e-chat__content">
        <VuePerfectScrollbar ref="scrollContainer" @ps-scroll-y="onScroll">
          <div class="e-chat__content-inner">
            <chat-item v-for="(msg, index) in messages" :key="index" :type="msg.type" :messages="msg.messages" :chat-id="chatId" />
          </div>
        </VuePerfectScrollbar>
        <div 
            class="e-chat__scroll-to-message"
            :class="{'e-chat__scroll-to-message--visible': showScrollDownButton}"
          >
          <div class="e-chat__scroll-to-message-btn e-btn e-btn--primary" @click="() => { scrollToBottom() }">
            <div class="e-chat__scroll-to-message--icon">
              <icon-arrow-down />
            </div>
          </div>
          <div class="e-badge" :class="{ 'e-badge--visible': unreadMessageIds.length > 0 }">
            <div class="e-badge__item">
              {{ unreadMessageIds.length }}
            </div>
          </div>
        </div>
        <the-chat-typing-indicator v-if="currentUserChat && typingState && !hideTypingState"
          :text="`${currentUserChat.counselor}  schreibt`" />
        <div v-if="showEmojiWidget">
          <EmojiPicker @onSelect="addEmoji" />
        </div>
      </div>
      <div class="chat__attachment-dialog">
        <AttachmentDialog ref="attachmentDialog" v-if="uploadActiveChat" :show.sync="showAttachmentWidget" :id="chatId" @update:attachments="(data) => attachments = data"></AttachmentDialog>
      </div>

      <footer v-show="!isDisabled" class="e-chat__footer">
        <label class="emoji-icon" :class="{'widget-open' : showEmojiWidget}" @click="() => { showEmojiWidget = !showEmojiWidget }">
          <IconEmoji />
        </label>
        <label v-if="uploadActiveChat" class="attachment-icon" :aria-label="$t('chat.addAttachment')">
            <input
              type="file"
              multiple
              @change="handleSelectFiles"
            />
          <IconPaperclip />
        </label>
        <autosize-textarea v-model="chatTextMessage" :placeholder="$t('chat.inputPlaceholder')" name="chatTextMessage"
          cols="10" rows="1" class="e-chat__input" @input.native="ev => (charCount = chatTextMessage.length)"
          @keydown.native="startTypingStatus" @keydown.enter.native="handleKeyupEnter" ref="textMessageElement" />
        <div class="e-chat__submit">
          <button :disabled="!canSend" :class="{ 'is-disabled': !canSend }" type="submit" class="e-btn e-btn--primary"
            @click.prevent="submitMessage">
            {{ $t('chat.submit') }}
          </button>
        </div>
        <div :class="{
          'e-dialog__charcount--alert': maxCharsExceeded
        }" class="e-dialog__charcount">
          {{ charCountString }}
        </div>
      </footer>
    </main>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { mapActions, mapGetters } from 'vuex'

import api from 'api'
import { mercureHubUrl } from 'api/config'
import ChatItem from 'molecules/ChatItem/ChatItem'
import EmojiPicker from 'molecules/EmojiPicker/EmojiPicker.vue'
import AttachmentDialog from 'molecules/AttachmentDialog/AttachmentDialog'
import TheChatTypingIndicator from 'atoms/TheChatTypingIndicator/TheChatTypingIndicator'
import AutosizeTextarea from '../../../shared/AutosizeTextarea'
import IconArrowDown from 'assets/icons/arrow-down.svg'
import IconEmoji from 'assets/icons/emoji-icon.svg'
import IconPaperclip from 'assets/icons/paperclip_icon.svg'
import IconClose from 'assets/icons/close.svg'

import VolumeButton from 'atoms/VolumeButton/VolumeButton'

export default {
  /** ========================================== */
  /*     COMPONENTS                              */
  /** ========================================== */
  components: {
    AttachmentDialog,
    AutosizeTextarea,
    ChatItem,
    EmojiPicker,
    IconEmoji,
    IconClose,
    IconPaperclip,
    VuePerfectScrollbar,
    TheChatTypingIndicator,
    IconArrowDown,
    VolumeButton
  },

  props: {
    propMessages: {
      type: Array,
      default: () => [],
      required: false
    },
    propId: {
      type: String,
      default: null,
      required: false
    },
    isDisabled: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  /** ========================================== */
  /*     DATA                                    */
  /** ========================================== */
  data: () => ({
    attachments: [],
    chatTextMessage: '',
    chatData: {
      id: null,
      messages: []
    },
    charCount: 0,
    maxChars: 500,
    hideTypingState: false,
    typingTimer: null,
    oponentTypingTimer: null,
    sendingMessage: false,
    readingClosedMessage: false,
    showScrollDownButton: false,
    showAttachmentWidget: false,
    showEmojiWidget: false,
    messagesSeen: new Map(),
    maxFiles: 5,
    maxFileSize: 10,
    acceptedFiles: [
      'application/pdf',
      'image/jpeg',
      'image/png'
    ]
  }),

  /** ========================================== */
  /*     COMPUTED                                */
  /** ========================================== */
  computed: {
    ...mapGetters([
      'userInfo',
      'currentChatEvent',
      'currentUserChat',
      'showClosedMessage',
      'typingState',
      'hasActiveChat',
      'chatIsOpen',
      'landingTexts',
      'uploadActiveChat'
    ]),

    chatId() {
      if (!this.currentUserChat) {
        return null
      }
      return this.currentUserChat.id
    },

    charCountString() {
      return `${this.charCount} / ${this.maxChars}`
    },

    maxCharsExceeded() {
      return this.charCount > this.maxChars
    },

    canSend() {
      if (
        this.maxCharsExceeded ||
        (this.charCount === 0 && this.attachments.length === 0) ||
        this.disabled ||
        this.sendingMessage
      ) {
        return false
      }
      return true
    },

    chatTitle() {
      let title = this.$t('chat.newChatTitle')
      if (this.currentChatEvent && this.currentChatEvent.subject) {
        title = this.currentChatEvent.subject + ': ' + title
      }
      if (this.currentUserChat !== null) {
        switch (this.currentUserChat.status) {
          case 'pending':
            title = this.$t('chat.waitingForChatAssignment')
            title += ` ${this.$t('chat.position')} ${this.currentUserChat.waitingPosition
              } ${this.$t('chat.positionOf')} ${this.currentUserChat.waitingQueue
              }`
            break
          case 'active':
            title = `${this.$t('chat.title')} ${this.currentUserChat.counselor}`
            break
        }
      }
      return title
    },

    messages() {
      if (this.userInfo === undefined) {
        return []
      }
      let messages = this.chatData.messages
      if (this.hasActiveChat && this.currentUserChat.messages.length > 0) {
        messages = this.currentUserChat.messages
      }

      const output = []
      messages.forEach((msg, i) => {
        if (i > 0 && output[output.length - 1].userId === msg.userId) {
          output[output.length - 1].messages.push({
            id: msg.id,
            text: msg.body,
            deleted: msg.isDeleted,
            createdAt: msg.createdAt,
            read: msg.read,
            attachments: msg.attachments
          })
        } else {
          output.push({
            type: msg.userId !== this.userInfo.id ? 'opponent' : 'user',
            userId: msg.userId,
            messages: [
              {
                id: msg.id,
                text: msg.body,
                deleted: msg.isDeleted,
                createdAt: msg.createdAt,
                read: msg.read,
                attachments: msg.attachments
              }
            ]
          })
        }
      })

      if (this.currentUserChat && this.currentUserChat.status === 'pending') {
        output.push({
          type: 'opponent',
          userId: null,
          messages: [
            {
              text: this.landingTexts.chatWaitingMessage,
              createdAt: new Date()
            }
          ]
        })
      }

      return output
    },

    unreadMessageIds() {
      if (this.userInfo === undefined) {
        return []
      }
      let messages = this.chatData.messages
      if (this.hasActiveChat && this.currentUserChat.messages.length > 0) {
        messages = this.currentUserChat.messages
      }

      return messages.filter((message) => !message.read && message.userId !== this.userInfo.id).map((message) => message.id)
    }
  },

  watch: {
    showClosedMessage: {
      handler: function (show) {
        if (show && !this.readingClosedMessage) {
          this.readingClosedMessage = true
          this.$alert(
            this.$t('chat.closedByCounselor'),
            this.$t('chat.closed'),
            {
              confirmButtonText: this.$t('system.ok')
            }
          ).finally(() => {
            api.call('closedChatInfoRead', this.currentUserChat.id)
            this.readingClosedMessage = true
            this.$router.push({ name: 'chatDialogs' })
          })
        }
      },
      immediate: true
    },
    unreadMessageIds(newVal, oldVal) {
      this.$nextTick(() => {
        this.onScroll()
        this.onUnreadMessageIdsChange(newVal, oldVal)
      })
    },
    typingState(typingState) {
      if (typingState) {
        this.hideTypingState = false
        clearTimeout(this.oponentTypingTimer)
        this.oponentTypingTimer = setTimeout(() => {
          this.hideTypingState = true
          this.oponentTypingTimer = null
        }, 30000)
      }
    },
  },
  created() {
    if (!this.chatIsOpen && !this.hasActiveChat) {
      this.$router.push({ name: 'chatDialogs' })
    }
  },
  /** ========================================== */
  /*     MOUNTED                                 */
  /** ========================================== */
  mounted() {
    this.scrollToBottom()
    if (this.propId) {
      this.chatData.id = this.propId
      this.chatData.messages = this.propMessages
      return
    }
    if (!this.hasActiveChat) {
      this.sendWelcomeMessage()
    }
      
    this.onUnreadMessageIdsChange(this.unreadMessageIds)
      
  },

  beforeDestroy() {
    clearTimeout(this.typingTimer)
    this.endTypingStatus()
  },

  /** ========================================== */
  /*     METHODS                                 */
  /** ========================================== */
  methods: {
    ...mapActions([
      'setInterruptedRoute',
      'startCurrentUserChat',
      'markChatMessageAsRead'
    ]),

    sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms))
    },
    async submitMessage() {
      if (!this.canSend) {
        return
      }
      this.sendingMessage = true
      this.showEmojiWidget = false
      const timestamp = new Date()
      let msg = {
        id: null,
        userId: this.userInfo.id,
        body: this.chatTextMessage,
        createdAt: timestamp,
        attachments: this.attachments
      }
      const formData = new FormData()
      formData.append('userId', this.userInfo.id)
      formData.append('body', this.chatTextMessage)
      formData.append('createdAt', timestamp)
      this.attachments.forEach(attachment => {
        console.log('file', attachment.file)
        formData.append('attachments[]', attachment.file)
      })
      let isStart = this.currentUserChat === null

      const response = await api
        .call(
          'sendChatMessage',
          this.hasActiveChat ? this.currentUserChat.id : null,
          formData
        )
        .finally(() => {
          this.sendingMessage = false

        })
      
        if (this.$refs.attachmentDialog) {
        this.$refs.attachmentDialog.reset()
      }

      if (response && response.data) {
        this.startCurrentUserChat(response.data.id)
      }

      if (isStart || this.currentUserChat.status === 'pending') {
        this.addMessage(msg)
      }

      this.chatTextMessage = ''
      this.charCount = 0
      this.attachments = []
      setTimeout(() => this.scrollToBottom(), 10)
    },

    setupMessageIntersectionObserver() {
      this.messageIntersectionObserver = new IntersectionObserver(
        (elements) => {
          const idsToMark = new Set();
          for (const el of elements) {
            if (!el.isIntersecting) continue;

            const id = el.target.dataset.id;
            if (!id || !this.unreadMessageIds.includes(id)) continue;

            if (!this.messagesSeen.has(id)) {
              this.messagesSeen.set(id, { header: false, footer: false });
            }

            const record = this.messagesSeen.get(id);

            const isHeader = el.target.classList.contains('e-chat__message__hidden-header');
            const isFooter =
              el.target.tagName === 'TIME';

            if (isHeader) record.header = true;
            if (isFooter) record.footer = true;

            if (record.header && record.footer) {
              idsToMark.add(id);
            }
          }

          if (idsToMark.size > 0) {
            this.markAsRead(Array.from(idsToMark));
          }
        },
        {
          root: this.$refs.scrollContainer.$el,
          threshold: 1.0
        }
      );
    },

    onUnreadMessageIdsChange(newVal, oldVal) {
      if (!this.messageIntersectionObserver) {
        this.setupMessageIntersectionObserver()
        setTimeout(() => this.onUnreadMessageIdsChange(newVal, oldVal), 100)
        return
      }
      const toUnobserve = oldVal != undefined ? oldVal.filter((id) => !newVal.includes(id)) : []
      const toObserve = newVal
      if (toUnobserve.length > 0) {
        for (const id of toUnobserve) {
          const els = [...this.$el.querySelectorAll(`[data-id='${id}']`)]
          els.map(el => this.messageIntersectionObserver.unobserve(el))
        }
      }
      if (toObserve.length > 0) {
        for (const id of toObserve) {
          const els = [...this.$el.querySelectorAll(`[data-id='${id}']`)]
          els.map(el => this.messageIntersectionObserver.observe(el))
        }
      }
    },

    addEmoji(emoji) {
      const textArea = this.$refs.textMessageElement.$el
      const startPos = textArea.selectionStart;
      const endPos = textArea.selectionEnd;
      this.chatTextMessage = this.chatTextMessage.substring(0, startPos)
        + emoji
        + this.chatTextMessage.substring(endPos, this.chatTextMessage.length);
      this.charCount = this.chatTextMessage.length
    },

    async markAsRead(messageIds) {
      for await (const id of messageIds) {
        await this.sleep(200);
        await api.call(
          'markChatMessageAsRead',
          { id, rootId: this.currentUserChat.id },
          null
        )
      }
    },

    handleKeyupEnter(ev) {
      if ((ev.metaKey || ev.ctrlKey) && ev.keyCode === 13) {
        this.submitMessage()
      }
    },

    addMessage(msg) {
      this.chatData.messages.push(msg)
    },

    handleDrop(event) {
      if(!this.uploadActiveChat) {
        return
      }
      this.$refs.attachmentDialog.addFiles(event.dataTransfer.files)
    },
    
    async processFiles(fileList) {
      if(fileList.length + this.attachments.length > this.maxFiles) {
        this.$message({
          type: 'warning',
          message: this.$t('system.uploadBox.fileLimitExceeded')
        })
        return
      }

      for (const file of fileList) {
        if(this.validateFile(file)) {
          await this.onUpload(file)
        }
      }
    },

    handleSelectFiles(event) {
      this.$refs.attachmentDialog.addFiles(event.target.files)
    },

    onScroll() {
      const scrollContainerEl = this.$refs.scrollContainer.$el

      this.showScrollDownButton = scrollContainerEl.scrollTop + scrollContainerEl.clientHeight + 10 < scrollContainerEl.scrollHeight
    },

    scrollToBottom() {
      if (this.$refs.scrollContainer) {
        this.$refs.scrollContainer.$el.scrollTop = this.$refs.scrollContainer.$el.scrollHeight
        this.$nextTick(() => this.showScrollDownButton = false)
      }
    },

    sendWelcomeMessage() {
      this.addMessage({
        id: null,
        body: this.$t('chat.welcomeMessage'),
        userId: null,
        createdAt: new Date()
      })
    },

    async finishChat() {
      if (this.currentUserChat.status === 'active') {
        this.$confirm(
          this.$t('chat.activeClosingConfirm'),
          this.$t('chat.close'),
          {
            confirmButtonText: this.$t('system.confirm'),
            cancelButtonText: this.$t('system.cancel'),
            type: 'warning'
          }
        ).then(async () => {
          api.call('closeChat', this.currentUserChat.id)
          this.$router.push({ name: 'chatDialogs' })
        })
        return
      }
      this.$confirm(
        this.$t('chat.pendingClosingConfirm'),
        this.$t('chat.cancel'),
        {
          confirmButtonText: this.$t('system.confirm'),
          cancelButtonText: this.$t('system.cancel'),
          type: 'warning'
        }
      ).then(async () => {
        api.call('cancelChat', this.currentUserChat.id)
        this.$router.push({ name: 'chatDialogs' })
      })
    },

    startTypingStatus: debounce(
      function () {
        if (this.currentUserChat && this.currentUserChat.status === 'active') {
          if (!this.typingTimer) {
            const body = new URLSearchParams({
              data: JSON.stringify({ userId: this.userInfo.id, value: true }),
              topic: `http://mercure.local/currentChat/${this.currentUserChat.id}`,
              private: 'on',
              type: 'typingState'
            })
            fetch(mercureHubUrl, {
              method: 'POST',
              mode: 'cors',
              body,
              credentials: 'include'
            })
          }
          clearTimeout(this.typingTimer)
          this.typingTimer = setTimeout(() => {
            this.typingTimer = null
            this.endTypingStatus()
          }, 1000)
        }
      },
      100,
      { leading: true, trailing: false }
    ),

    endTypingStatus: debounce(
      function () {
        if (this.currentUserChat && this.currentUserChat.status === 'active') {
          const body = new URLSearchParams({
            data: JSON.stringify({ userId: this.userInfo.id, value: false }),
            topic: `http://mercure.local/currentChat/${this.currentUserChat.id}`,
            private: 'on',
            type: 'typingState'
          })
          fetch(mercureHubUrl, {
            method: 'POST',
            mode: 'cors',
            body,
            credentials: 'include'
          })
        }
      },
      100,
      { leading: false, trailing: true }
    )
  }
}
</script>

<style lang="scss" scoped>
@import '_assets/TheChat';

.e-chat {
  &.on-drag {

    * {
      pointer-events: none;
    }

    .drop-overlay {
      display: block;

    }
  }
  .drop-overlay {
    display: none;
    position: absolute;
    width: 100%;
    height: 100%;
    pointer-events: none;
    background-color: hsla(0, 50%, 10%, 5%);
    outline: 2px dashed #333;
    inset: 2px;
    right: 0;
    top: 1px;
    left: 1px;
    bottom: 1px;
    border-radius: 4px;

  }
}
</style>
